/*
 * Rewards — storefront client.
 *
 * Talks to Brand OS through the Shopify App Proxy at /apps/rewards/*. Because
 * that path is same-origin there is no CORS, no API key in the theme, and no
 * way for a shopper to point it at another store: Shopify signs every request
 * and tells the server who is logged in.
 *
 * Nothing here is trusted by the server. The balance shown, the cart total sent
 * and the points requested are all re-checked server side; this file is a view.
 *
 * Two surfaces:
 *   • the cart strip  — balance, "use points", and what it takes off
 *   • the widget      — a launcher and panel with tier, history and referral
 *
 * State is fetched once per open and after any change, never polled.
 */
(function () {
  'use strict'

  var BASE = '/apps/rewards'
  var TOKEN_KEY = 'rw_token'

  var state = null          // last /state response
  var loading = false

  /* ── plumbing ─────────────────────────────────────────────────────────── */

  function token() {
    try { return localStorage.getItem(TOKEN_KEY) || '' } catch (e) { return '' }
  }
  function setToken(t) {
    try { t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY) } catch (e) {}
  }

  function url(path, params) {
    var q = new URLSearchParams(params || {})
    if (token()) q.set('t', token())
    var s = q.toString()
    return BASE + path + (s ? '?' + s : '')
  }

  function get(path, params) {
    return fetch(url(path, params), { credentials: 'same-origin' }).then(function (r) { return r.json() })
  }

  function post(path, body) {
    return fetch(url(path), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {}),
    }).then(function (r) { return r.json() })
  }

  /** Cart total in rupees, read from Shopify's own cart rather than the DOM. */
  function cartTotal() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json() })
      .then(function (c) { return (c.total_price || 0) / 100 })
      .catch(function () { return 0 })
  }

  /*
   * Apply a discount code to the live cart.
   *
   * Shopify's AJAX cart API cannot set a discount, but /discount/CODE sets the
   * cart's discount cookie. Fetching it has the same effect without leaving the
   * page, which is what lets the drawer stay open and show the new total.
   */
  function applyCode(code) {
    return fetch('/discount/' + encodeURIComponent(code), { credentials: 'same-origin' })
      .catch(function () {})
  }

  /*
   * Put the code on the theme's own checkout button.
   *
   * This helps ONLY when that button is the one the customer actually clicks.
   * When Shiprocket injects its "Buy Now" (.shiprocket-headless /
   * .sr-headless-checkout) the theme hides .shell-cart__checkout entirely —
   * see the :has() rule in header.liquid — and the customer leaves through the
   * SDK's button, which we do not own and must not rewrite.
   *
   * So this is a bonus path, not the mechanism. The mechanism is the discount
   * sitting on the Shopify cart itself, which is what verifyApplied() checks.
   */
  function armCheckout(code) {
    if (!code) return
    var links = document.querySelectorAll('.shell-cart__checkout, [data-rw-checkout]')
    for (var i = 0; i < links.length; i++) {
      links[i].setAttribute('href', '/discount/' + encodeURIComponent(code) + '?redirect=/checkout')
    }
  }

  /*
   * Did the discount actually land on the cart?
   *
   * This is the question that matters, and it is answerable: /cart.js reports
   * cart-level discounts once a code has been applied. If it shows one, any
   * checkout built from this cart — the theme's, or Shiprocket's headless one —
   * inherits it. If it does not, the customer must type the code in themselves,
   * and they need to be TOLD that rather than discovering it on the payment
   * page with points already spent.
   *
   * Deliberately checks the cart rather than assuming the fetch worked: a 200
   * from /discount only means Shopify accepted the request.
   */
  function verifyApplied() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json() })
      .then(function (c) {
        var codes = c.discount_codes || []
        var apps = c.cart_level_discount_applications || []
        var applicable = codes.filter(function (d) { return d.applicable !== false })
        return applicable.length > 0 || apps.length > 0 || Number(c.total_discount || 0) > 0
      })
      .catch(function () { return false })
  }

  var money = function (n) {
    return '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })
  }

  /* ── state ────────────────────────────────────────────────────────────── */

  function refresh() {
    if (loading) return Promise.resolve(state)
    loading = true
    return cartTotal().then(function (total) {
      return get('/state', { cart: String(total) })
    }).then(function (s) {
      state = s
      loading = false
      render()
      document.dispatchEvent(new CustomEvent('rewards:state', { detail: s }))
      return s
    }).catch(function () {
      loading = false
      return null
    })
  }

  /* ── rendering ────────────────────────────────────────────────────────── */

  function render() {
    renderCartStrip()
    renderWidget()
    // The drawer's markup is replaced wholesale on every cart change, which
    // rebuilds the checkout link from Liquid. Re-arm it each time so a
    // redemption made three edits ago is still carried to checkout.
    try { armCheckout(sessionStorage.getItem('rw_code')) } catch (e) {}
  }

  function renderCartStrip() {
    var el = document.querySelector('[data-rewards-cart]')
    if (!el) return
    if (!state || !state.enabled) { el.hidden = true; return }
    el.hidden = false

    var p = state.program
    var m = state.member

    if (!m) {
      // Not identified. Pitch the programme rather than showing an empty
      // balance — a zero next to a login prompt reads as "you have nothing".
      el.innerHTML =
        '<div class="rw-strip rw-strip--join">' +
          '<div class="rw-strip__copy">' +
            '<strong>' + esc(p.name) + '</strong>' +
            '<span>Earn ' + p.pointsPer100 + ' ' + esc(p.pointName) + ' on every ' + money(100) + ' you spend.</span>' +
          '</div>' +
          '<button type="button" class="rw-btn" data-rw-login>Sign in</button>' +
        '</div>'
      return
    }

    var r = m.redeemable || { points: 0, value: 0 }
    var canRedeem = r.points > 0

    /*
     * Already redeemed on this cart? Show the code rather than another Apply
     * button.
     *
     * The code is shown deliberately, not hidden: if the automatic apply does
     * not survive the hand-off to checkout, the customer can type it into the
     * coupon field themselves instead of losing points and getting nothing.
     */
    var applied = null
    var stuck = false
    try {
      applied = sessionStorage.getItem('rw_code')
      stuck = sessionStorage.getItem('rw_applied') === '1'
    } catch (e) {}

    if (applied) {
      el.innerHTML =
        '<div class="rw-strip ' + (stuck ? 'rw-strip--applied' : 'rw-strip--warn') + '">' +
          '<div class="rw-strip__copy">' +
            '<strong>' + (stuck ? 'Discount applied' : 'Use this code at checkout') + '</strong>' +
            '<span>' +
              (stuck
                ? 'Code <code>' + esc(applied) + '</code> is on your cart.'
                : 'Enter <code>' + esc(applied) + '</code> in the coupon box at checkout to get your discount.') +
            '</span>' +
          '</div>' +
          '<button type="button" class="rw-btn" data-rw-copy="' + esc(applied) + '">Copy</button>' +
        '</div>' +
        '<div class="rw-strip__note">' + Number(m.balance).toLocaleString('en-IN') + ' ' +
          esc(p.pointName) + ' left.</div>'
      return
    }

    el.innerHTML =
      '<div class="rw-strip">' +
        '<div class="rw-strip__copy">' +
          '<strong>' + Number(m.balance).toLocaleString('en-IN') + ' ' + esc(p.pointName) + '</strong>' +
          '<span>' + (canRedeem
            ? 'Use them for ' + money(r.value) + ' off this order'
            : 'Minimum ' + p.minRedeem + ' ' + esc(p.pointName) + ' to redeem') +
          '</span>' +
        '</div>' +
        (canRedeem
          ? '<button type="button" class="rw-btn rw-btn--primary" data-rw-redeem="' + r.points + '">Apply</button>'
          : '') +
      '</div>' +
      (m.pending > 0
        ? '<div class="rw-strip__note">' + Number(m.pending).toLocaleString('en-IN') +
          ' more on the way — they unlock when your last order is delivered.</div>'
        : '')
  }

  function renderWidget() {
    var panel = document.querySelector('[data-rewards-panel]')
    if (!panel) return

    // The launcher ships hidden and is revealed only once the programme answers
    // that it is live. A "Rewards" button that opens an empty panel is worse
    // than no button at all.
    var launcher = document.querySelector('[data-rewards-launcher]')
    var live = !!(state && state.enabled)
    if (launcher) launcher.hidden = !live
    if (!live) return

    var p = state.program
    var m = state.member

    if (!m) {
      panel.innerHTML = header(p.name) +
        '<div class="rw-panel__body">' +
          '<p class="rw-lead">Join ' + esc(p.name) + ' and earn ' + p.pointsPer100 + ' ' +
            esc(p.pointName) + ' on every ' + money(100) + '.</p>' +
          loginForm() +
        '</div>'
      return
    }

    var tier = m.tier
    var next = m.nextTier

    panel.innerHTML = header(p.name) +
      '<div class="rw-panel__body">' +
        '<div class="rw-balance">' +
          '<div class="rw-balance__n">' + Number(m.balance).toLocaleString('en-IN') + '</div>' +
          '<div class="rw-balance__l">' + esc(p.pointName) + ' · worth ' + money(m.worth) + '</div>' +
          (m.pending > 0 ? '<div class="rw-pending">' + Number(m.pending).toLocaleString('en-IN') +
            ' pending until delivery</div>' : '') +
        '</div>' +

        (tier ? '<div class="rw-tier"><span class="rw-tier__badge">' + esc(tier.name) + '</span>' +
          (tier.multiplier > 1 ? '<span>Earning ' + tier.multiplier + '× ' + esc(p.pointName) + '</span>' : '') +
          '</div>' : '') +

        (next ? '<div class="rw-next">Spend ' + money(next.remaining) + ' more to reach <strong>' +
          esc(next.name) + '</strong></div>' : '') +

        (p.referral && p.referral.enabled && m.referralCode
          ? referBlock(p, m)
          : '') +

        '<div class="rw-history">' +
          '<div class="rw-history__title">Your activity</div>' +
          (m.history && m.history.length
            ? m.history.map(function (h) {
                return '<div class="rw-history__row' + (h.state === 'pending' ? ' is-pending' : '') + '">' +
                  '<span>' + esc(h.reason || h.kind) +
                    (h.state === 'pending' ? ' <em>(pending)</em>' : '') + '</span>' +
                  '<strong>' + (h.points > 0 ? '+' : '') + h.points + '</strong>' +
                '</div>'
              }).join('')
            : '<p class="rw-empty">Nothing yet.</p>') +
        '</div>' +

        '<button type="button" class="rw-link" data-rw-logout>Sign out</button>' +
      '</div>'
  }

  /*
   * The invite block.
   *
   * Two versions, because the promise is materially different. A points reward
   * is store credit; a cash reward is money in their bank, and that needs a UPI
   * id — so the cash version has to ASK for one, and has to be honest that
   * nothing is sent until the friend's parcel is actually delivered.
   */
  function referBlock(p, m) {
    var r = p.referral
    var cash = r.rewardType === 'cash'
    var earns = cash ? money(r.referrerCash) + ' in your UPI' : r.referrerPoints + ' ' + esc(p.pointName)
    var c = m.referralCash || { pending: 0, payable: 0, paid: 0 }

    var out =
      '<div class="rw-refer">' +
        '<div class="rw-refer__title">Invite a friend</div>' +
        '<p>They get ' + (r.discountType === 'percent'
          ? r.discountValue + '% off'
          : money(r.discountValue) + ' off') +
          ' their first order over ' + money(r.minOrder) +
          '. You get <strong>' + earns + '</strong> once it is delivered.</p>' +
        '<div class="rw-code"><code>' + esc(m.referralCode) + '</code>' +
          '<button type="button" class="rw-btn" data-rw-share="' + esc(m.referralCode) + '">Share</button></div>'

    if (cash) {
      // Their running position, only when there is something to say.
      if (c.pending > 0 || c.payable > 0 || c.paid > 0) {
        out += '<div class="rw-refer__cash">' +
          (c.pending > 0 ? '<span>' + money(c.pending) + ' waiting on delivery</span>' : '') +
          (c.payable > 0 ? '<span class="is-due">' + money(c.payable) + ' being sent to you</span>' : '') +
          (c.paid > 0 ? '<span class="is-paid">' + money(c.paid) + ' paid</span>' : '') +
        '</div>'
      }

      out += m.payoutUpi
        ? '<div class="rw-upi-set">Paying to <code>' + esc(m.payoutUpi) + '</code>' +
            '<button type="button" class="rw-link" data-rw-upi-edit>Change</button></div>' +
          '<form class="rw-upi" data-rw-upi-form hidden>' +
            '<input name="upi" placeholder="yourname@bank" autocomplete="off">' +
            '<button type="submit" class="rw-btn">Save</button>' +
            '<p class="rw-msg" data-rw-msg hidden></p>' +
          '</form>'
        : '<form class="rw-upi" data-rw-upi-form>' +
            '<label>Where should we send it?</label>' +
            '<input name="upi" placeholder="yourname@bank" autocomplete="off">' +
            '<button type="submit" class="rw-btn">Save UPI</button>' +
            '<p class="rw-msg" data-rw-msg hidden></p>' +
          '</form>'
    }

    return out + '</div>'
  }

  function header(name) {
    return '<div class="rw-panel__head"><span>' + esc(name) + '</span>' +
      '<button type="button" class="rw-close" data-rw-close aria-label="Close">×</button></div>'
  }

  function loginForm() {
    return '<form class="rw-login" data-rw-otp-form>' +
      '<label>Mobile number' +
        '<input type="tel" name="phone" inputmode="numeric" maxlength="10" placeholder="10-digit number" required>' +
      '</label>' +
      '<div class="rw-otp-step" hidden>' +
        '<label>Enter the code we sent' +
          '<input type="text" name="code" inputmode="numeric" maxlength="6" placeholder="6-digit code">' +
        '</label>' +
      '</div>' +
      '<button type="submit" class="rw-btn rw-btn--primary rw-btn--full">Continue</button>' +
      '<p class="rw-msg" data-rw-msg hidden></p>' +
    '</form>'
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    })
  }

  /* ── interactions ─────────────────────────────────────────────────────── */

  function openPanel() {
    var w = document.querySelector('[data-rewards-widget]')
    if (w) w.classList.add('is-open')
    refresh()
  }
  function closePanel() {
    var w = document.querySelector('[data-rewards-widget]')
    if (w) w.classList.remove('is-open')
  }

  function msg(form, text, bad) {
    var el = form.querySelector('[data-rw-msg]')
    if (!el) return
    el.textContent = text
    el.hidden = !text
    el.className = 'rw-msg' + (bad ? ' is-bad' : '')
  }

  document.addEventListener('click', function (e) {
    var t = e.target.closest ? e.target : e.target.parentElement
    if (!t || !t.closest) return

    if (t.closest('[data-rewards-launcher]')) { e.preventDefault(); openPanel(); return }
    if (t.closest('[data-rw-close]')) { e.preventDefault(); closePanel(); return }
    if (t.closest('[data-rw-login]')) { e.preventDefault(); openPanel(); return }

    if (t.closest('[data-rw-logout]')) {
      e.preventDefault()
      setToken('')
      refresh()
      return
    }

    if (t.closest('[data-rw-upi-edit]')) {
      e.preventDefault()
      var f = document.querySelector('[data-rw-upi-form]')
      if (f) { f.hidden = false; var i = f.querySelector('input'); if (i) i.focus() }
      return
    }

    var copy = t.closest('[data-rw-copy]')
    if (copy) {
      e.preventDefault()
      var val = copy.getAttribute('data-rw-copy')
      if (navigator.clipboard) {
        navigator.clipboard.writeText(val).then(function () {
          copy.textContent = 'Copied'
          setTimeout(function () { copy.textContent = 'Copy' }, 1800)
        })
      }
      return
    }

    var share = t.closest('[data-rw-share]')
    if (share) {
      e.preventDefault()
      var code = share.getAttribute('data-rw-share')
      var link = window.location.origin + '/?ref=' + encodeURIComponent(code)
      if (navigator.share) {
        navigator.share({ title: 'Shop with my code', text: 'Use ' + code + ' for a discount.', url: link })
          .catch(function () {})
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(link).then(function () {
          share.textContent = 'Copied'
          setTimeout(function () { share.textContent = 'Share' }, 1800)
        })
      }
      return
    }

    var redeem = t.closest('[data-rw-redeem]')
    if (redeem) {
      e.preventDefault()
      var points = Number(redeem.getAttribute('data-rw-redeem'))
      redeem.disabled = true
      redeem.textContent = 'Applying…'
      cartTotal().then(function (total) {
        return post('/redeem', { points: points, cartTotal: total })
      }).then(function (r) {
        if (!r || !r.ok) {
          redeem.disabled = false
          redeem.textContent = 'Apply'
          alert((r && r.error) || 'Could not apply your points')
          return
        }
        // Remember the code for this cart. If the drawer re-renders (the
        // Section Rendering API replaces its markup wholesale) the checkout
        // button is rebuilt from Liquid and loses the href — so the code is
        // stored and re-armed on every render, not just this one.
        try { sessionStorage.setItem('rw_code', r.code) } catch (e) {}

        // Apply the code, then CHECK the cart really carries a discount before
        // claiming anything. The result decides what the customer is told.
        return applyCode(r.code)
          .then(verifyApplied)
          .then(function (stuck) {
            try { sessionStorage.setItem('rw_applied', stuck ? '1' : '0') } catch (e) {}
            armCheckout(r.code)
            document.dispatchEvent(new CustomEvent('cart:refresh'))
            return refresh()
          })
      })
      return
    }
  })

  document.addEventListener('submit', function (e) {
    // Saving a UPI id for cash referral rewards.
    var upiForm = e.target.closest ? e.target.closest('[data-rw-upi-form]') : null
    if (upiForm) {
      e.preventDefault()
      var upi = (upiForm.querySelector('[name="upi"]') || {}).value || ''
      msg(upiForm, 'Saving…')
      post('/payout-method', { upi: upi }).then(function (r) {
        if (!r || !r.ok) return msg(upiForm, (r && r.error) || 'Could not save that', true)
        msg(upiForm, '')
        refresh()
      })
      return
    }

    var form = e.target.closest ? e.target.closest('[data-rw-otp-form]') : null
    if (!form) return
    e.preventDefault()

    var phone = (form.querySelector('[name="phone"]') || {}).value || ''
    var step = form.querySelector('.rw-otp-step')
    var codeInput = form.querySelector('[name="code"]')
    var sent = step && !step.hidden

    if (!sent) {
      msg(form, 'Sending…')
      post('/otp/send', { phone: phone }).then(function (r) {
        if (!r || !r.ok) return msg(form, (r && r.error) || 'Could not send a code', true)
        step.hidden = false
        if (codeInput) codeInput.focus()
        msg(form, 'Code sent to ' + phone)
      })
      return
    }

    msg(form, 'Checking…')
    post('/otp/verify', { phone: phone, code: (codeInput || {}).value || '' }).then(function (r) {
      if (!r || !r.ok) return msg(form, (r && r.error) || 'That code is not right', true)
      setToken(r.token)
      msg(form, '')
      refresh()
    })
  })

  /*
   * A referral link (?ref=CODE) drops the code into the cart the moment someone
   * arrives, so the friend never has to remember to type it. It is applied as a
   * discount code, which is also how the server later recognises the referral.
   */
  function captureRef() {
    var ref = new URLSearchParams(window.location.search).get('ref')
    if (!ref) return
    try {
      if (sessionStorage.getItem('rw_ref') === ref) return
      sessionStorage.setItem('rw_ref', ref)
    } catch (e) {}
    applyCode(ref)
  }

  /* ── boot ─────────────────────────────────────────────────────────────── */

  function boot() {
    captureRef()
    refresh()
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot)
  else boot()

  // The drawer re-renders through the Section Rendering API, which replaces the
  // strip's markup — so redraw whenever the theme says the cart changed.
  document.addEventListener('cart:refresh', function () { setTimeout(refresh, 60) })

  window.Rewards = { refresh: refresh, open: openPanel, state: function () { return state } }
})()
